import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { FormResponseErrorType, FormUtils, useFormContext } from '../../../../components/molecules/Form';
import { Namespace } from '../../../../translation/namespace';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import { translatePartOfStringWithLink } from '../../../../lib/StringUtils/StringHTMLUtils';
import useRedirect from '../../../../hooks/useRedirect';
import ModalContext from '../../../../components/organisms/ModalDialog/ModalContext';
import SortableList from '../../../../components/molecules/SortableList/SortableList';
import FormSelect, { optionsProcessor } from '../../../../components/molecules/Form/components/FormSelect/FormSelect';
import FormsRow from '../../../../components/molecules/Form/components/FormsRow/FormsRow';
import { murmurhash } from '../../../../lib/js/hash';
import { StatusInfoError } from '../../../../_lib/components/StatusInfo';
import { FormSubmitButton } from '../../../../components/molecules/Form/components/FormSubmitButton/FormSubmitButton';

// valid 'errorType' in response from backend found und SortChildPages.
const ERROR_TYPE = {
  INVALID_CHILD_PAGES: 'invalidChildPages',
};

const SELECT_OPTIONS = {
  SUB_CONTENTS: 'subContents',
  SUB_SECTIONS: 'subSections',
};

const resetChanges = (checkBoxParam, listParam) => {
  checkBoxParam.reset(true);
  listParam.reset(true);
};

const hasChanges = (checkBoxParam, listParam) => {
  return checkBoxParam.valueChanged() || listParam.valueChanged();
};

function SortChildPagesFormTemplate() {
  const { inputs, onChange, status, getStatus, submitActionEnabled, response } = useFormContext();
  const { t } = useTranslation(Namespace.SortChildPagesForm);
  const location = useLocation();
  const [startRedirect] = useRedirect();
  const { preventModalClose, closePreventedMessage } = useContext(ModalContext);
  const [dropDown, setDropDown] = useState(
    inputs.subContents ? SELECT_OPTIONS.SUB_CONTENTS : SELECT_OPTIONS.SUB_SECTIONS
  );

  const getForwardMessage = () => {
    return translatePartOfStringWithLink(t(`form.status.successForward`), location.pathname, false);
  };

  const getErrorMessage = key => FormUtils.createStatusMessage(t(`form.status.${key}`), key);

  const statusMessageWithResponse = {
    [InteractionStatus.SUCCESS]: [
      FormUtils.createStatusMessage(t(`form.status.success`), InteractionStatus.SUCCESS),
      FormUtils.createStatusMessage(getForwardMessage(), InteractionStatus.SENDING),
    ],
    [InteractionStatus.FAIL]: getErrorMessage(InteractionStatus.FAIL),
    [FormResponseErrorType.PAGE_LOCKED]: FormUtils.createStatusMessage(
      translatePartOfStringWithLink('phrases:pageLocked', response?.data.pageLockedBy?.url, false, {
        name: response?.data.pageLockedBy?.name,
      })
    ),
    [ERROR_TYPE.INVALID_CHILD_PAGES]: getErrorMessage(ERROR_TYPE.INVALID_CHILD_PAGES),
  };

  useEffect(() => {
    if (status === InteractionStatus.SENDING) {
      preventModalClose(true);
    }

    if (status === InteractionStatus.SUCCESS) {
      startRedirect(location.pathname);
    }

    if (status === InteractionStatus.FAIL) {
      preventModalClose(false);
    }
  }, [location, preventModalClose, startRedirect, status]);

  const getHeading = () => {
    return t(`${Namespace.MainNavigation}:groupLabel.${dropDown}`);
  };

  /** Callback function to handle changes inside SortableList
   *
   * 1. Checkbox is toggled
   * 2. List is updated
   */
  const handleOnChange = (name, value) => {
    if (
      [SELECT_OPTIONS.SUB_SECTIONS, SELECT_OPTIONS.SUB_SECTIONS].includes(name) &&
      !value &&
      inputs[name].valueChanged()
    ) {
      inputs[name].reset();
    }
    onChange(name, value);
  };

  const memorizedHash = useMemo(
    () => murmurhash(inputs.subContents + inputs.subSections),
    [inputs.subContents, inputs.subSections]
  );

  return (
    <>
      {inputs.subContents && inputs.subSections && (
        <FormsRow>
          <FormSelect
            id="dropDown"
            name="dropDown"
            required={false}
            labelTitle={t('inputs.dropDownSelect.label')}
            ariaControls="soratable-list-container"
            value={dropDown}
            options={optionsProcessor(
              'dropDownSelect',
              [
                {
                  value: SELECT_OPTIONS.SUB_CONTENTS,
                },
                {
                  value: SELECT_OPTIONS.SUB_SECTIONS,
                },
              ],
              Namespace.SortChildPagesForm
            )}
            hidePlaceHolder
            onChange={({ target }) => {
              setDropDown(target.value);
            }}
          />
        </FormsRow>
      )}
      <div id="soratable-list-container">
        {inputs.subContents && dropDown === SELECT_OPTIONS.SUB_CONTENTS && (
          <SortableList
            id={`subcontents-${memorizedHash}`}
            name={inputs.subContents.name}
            heading={getHeading()}
            sortableItemName={t('form.sortableItemName')}
            initialList={inputs.subContents.originalValue}
            intitiallySorted={inputs.sortAlphabeticallySubContents.originalChecked}
            sortableList={inputs.subContents.value}
            preparedValue={inputs.subContents.preparedValue}
            sortAlphabeticallyCheckBox={inputs.sortAlphabeticallySubContents}
            // callbacks
            onChange={handleOnChange}
            resetChanges={() => resetChanges(inputs.sortAlphabeticallySubContents, inputs.subContents)}
            hasChanges={() => hasChanges(inputs.sortAlphabeticallySubContents, inputs.subContents)}
          />
        )}
        {inputs.subSections && dropDown === SELECT_OPTIONS.SUB_SECTIONS && (
          <SortableList
            id={`subsections-${memorizedHash}`}
            name={inputs.subSections.name}
            heading={getHeading()}
            sortableItemName={t('form.sortableItemName')}
            initialList={inputs.subSections.originalValue}
            intitiallySorted={inputs.sortAlphabeticallySubSections.originalChecked}
            sortableList={inputs.subSections.value}
            preparedValue={inputs.subSections.preparedValue}
            sortAlphabeticallyCheckBox={inputs.sortAlphabeticallySubSections}
            // callbacks
            onChange={handleOnChange}
            resetChanges={() => resetChanges(inputs.sortAlphabeticallySubSections, inputs.subSections)}
            hasChanges={() => hasChanges(inputs.sortAlphabeticallySubSections, inputs.subSections)}
          />
        )}
      </div>
      <FormSubmitButton buttonLabel={t('form.submit')} enabled={submitActionEnabled()} />
      <LiveRegion invisible={false} htmlTag="div">
        {getStatus(statusMessageWithResponse)}
        {closePreventedMessage && <StatusInfoError message={closePreventedMessage} modifier="right" />}
      </LiveRegion>
    </>
  );
}

export default SortChildPagesFormTemplate;
